import { exists, UnreachableError } from '../helpers';
import { fromNested, Nested, toNested } from './nested';
import {
  Component,
  ComponentStyle,
  NestedNode,
  NestedTagNode,
  Site,
  SiteStyle,
} from './schema';
import { MAGNITUDE_NAMES, MARGIN_STYLES, SPACER_STYLES } from './spacer_styles';
import {
  approxSiteStyleToComponent,
  componentStyleTypeclass,
  mergeStyles,
  resolveComboClass,
  siteStyleTypeclass,
} from './style_util';

/** If the node is a spacing wrapper, returns the direction & magnitude of the margin */
function detectSpacingWrapper(
  node: NestedNode,
  stylesById: ReadonlyMap<string, ComponentStyle>,
):
  | { magnitude: (typeof MAGNITUDE_NAMES)[number]; direction: 'top' | 'bottom' }
  | undefined {
  if (node.text || (node.children.length || 0) === 0) return; // empty node
  if (node.classes.length !== 2) return; // wrong number of classes
  const [c1, c2] = node.classes.map((x: string) => stylesById.get(x)?.name);

  let direction: 'top' | 'bottom';
  if (c1 === 'margin-top') direction = 'top';
  else if (c1 == 'margin-bottom') direction = 'bottom';
  else return; // not a margin- node

  if (!c2?.startsWith('margin-')) return;
  const magnitude = c2.substring('margin-'.length);
  if (!MAGNITUDE_NAMES.includes(magnitude)) return;
  return { direction, magnitude };
}

function hasGridOrFlexGap(
  node: NestedTagNode,
  stylesById: ReadonlyMap<string, ComponentStyle>,
  stylesTree: Nested<ComponentStyle>[],
): boolean {
  const less = getLessApplied(node.classes, stylesById, stylesTree);
  return cssHasGridOrFlexGap(less);
}

export function cssHasGridOrFlexGap(css: string): boolean {
  if (!css.match(/display: (|inline-|block |inline )(flex|grid);/)) {
    return false;
  }
  const matches = css.matchAll(/(gap|column-gap|row-gap): ([^;]+);/g);
  return [...matches].some((match) => {
    const magnitude = parseInt(match[1]);
    return magnitude !== 0;
  });
}

/**
 * Gets all the LESS (CSS) applied to the given nodes.
 *
 * LIMITATION: This doesn't consider specificity.  It doesn't remove attributes that are
 *   later overridden.
 */
function getLessApplied(
  ids: readonly string[],
  stylesById: ReadonlyMap<string, ComponentStyle>,
  stylesTree: Nested<ComponentStyle>[],
): string {
  const selectorNames = new Set(
    ids.map((id) => stylesById.get(id)?.name).filter(exists),
  );
  const appliedLess: string[] = [];
  function walk(node: Nested<ComponentStyle>) {
    if (!selectorNames.has(node.name)) return;
    appliedLess.push(node.styleLess);
    node.children.forEach(walk);
  }
  stylesTree.forEach(walk);
  return appliedLess.join('\n');
}

function convertToSpacingBlocks(
  nodes: readonly NestedNode[],
  styles: readonly ComponentStyle[],
  recordStyleUsed?: (id: string) => void,
): NestedNode[] {
  const stylesById = new Map(styles.map((x) => [x._id, x]));
  const stylesTree = toNested(styles);

  function transform(node: NestedNode): NestedNode {
    if (node.text) return node;
    const parentHasGap = hasGridOrFlexGap(node, stylesById, stylesTree);
    const newChildren = node.children.flatMap((child) => {
      const wrapper = detectSpacingWrapper(child, stylesById);
      if (wrapper == null || parentHasGap) {
        // If the parent has a "gap" value, inserting spacing blocks will duplicate
        // the gap (because 2 children instead of 1).  Leave them as margins.
        return transform(child);
      }
      const spacingBlock = {
        ...child,
        children: [],
        classes: resolveComboClass([`spacer-${wrapper.magnitude}`], stylesById),
      };
      spacingBlock.classes.forEach((id) => recordStyleUsed?.(id));
      if (wrapper.direction === 'top') {
        return [spacingBlock, ...child.children.map(transform)];
      } else if (wrapper.direction === 'bottom') {
        return [...child.children.map(transform), spacingBlock];
      } else {
        throw new UnreachableError(wrapper.direction);
      }
    });
    return { ...node, children: newChildren };
  }

  return nodes.map(transform);
}

/** If a node is a "spacing block", returns the magnitude of the spacing */
function detectSpacingBlock(
  node: NestedNode,
  stylesById: ReadonlyMap<string, ComponentStyle>,
): (typeof MAGNITUDE_NAMES)[number] | undefined {
  if (node.text || (node.children.length || 0) !== 0) return; // node has content
  if (node.classes.length !== 1) return; // wrong number of classes
  const c1 = stylesById.get(node.classes[0])?.name;
  if (!c1?.startsWith('spacer-')) return;
  const magnitude = c1.substring('spacer-'.length);
  if (!MAGNITUDE_NAMES.includes(magnitude)) return;
  return magnitude;
}

function convertToSpacingWrappers(
  nodes: readonly NestedNode[],
  styles: readonly ComponentStyle[],
  recordStyleUsed?: (id: string) => void,
): NestedNode[] {
  const stylesById = new Map(styles.map((x) => [x._id, x]));
  function transform(node: NestedNode): NestedNode {
    if (node.text) return node;
    const newChildren = node.children.map(transform);
    for (let i = 0; i < newChildren.length; i++) {
      const spacer = newChildren[i];
      const magnitude = detectSpacingBlock(spacer, stylesById);
      if (!magnitude) continue;
      console.log('found spacing block', magnitude);
      if (i > 0) {
        // convert this spacing block into a wrapper of previous node
        const contents = newChildren[i - 1];
        const wrapper = {
          ...spacer,
          children: [contents],
          classes: resolveComboClass(
            [`margin-bottom`, `margin-${magnitude}`],
            stylesById,
          ),
        };
        wrapper.classes.forEach((id) => recordStyleUsed?.(id));
        newChildren[i - 1] = wrapper;
        newChildren.splice(i, 1);
      } else if (i < newChildren.length - 1) {
        // convert this spacing block into a wrapper of next node
        const contents = newChildren.splice(i + 1, 1)[0];
        const wrapper = {
          ...spacer,
          children: [contents],
          classes: resolveComboClass(
            [`margin-top`, `margin-${magnitude}`],
            stylesById,
          ),
        };
        wrapper.classes.forEach((id) => recordStyleUsed?.(id));
        newChildren[i] = wrapper;
      } else {
        // leave this spacing block alone
      }
    }
    return { ...node, children: newChildren };
  }
  return nodes.map(transform);
}

/** Convert a component that uses spacing wrappers to use spacing blocks */
export function convertComponentToSpacingBlocks(component: Component) {
  const styles = mergeStyles(
    component.payload.styles,
    SPACER_STYLES.map(approxSiteStyleToComponent),
    componentStyleTypeclass,
  ).styles;
  const nodes = convertToSpacingBlocks(
    toNested(component.payload.nodes),
    styles,
  );
  return {
    ...component,
    payload: { ...component.payload, nodes: fromNested(nodes), styles },
  };
}

function addAffects(
  styles: readonly SiteStyle[],
  pageId: string,
  styleId: string,
) {
  return styles.map((s) => {
    if (s._id !== styleId) return s;
    const affects = {
      ...s.data.affects,
      [pageId]: (s.data.affects[pageId] || 0) + 1,
    };
    return { ...s, data: { ...s.data, affects } };
  });
}

/**
 * Convert a site that uses spacing wrappers to use spacing blocks.
 */
export function convertSiteToSpacingBlocks(site: Site, pageId: string): Site {
  let styles = mergeStyles(
    site.styles.blocks,
    SPACER_STYLES,
    siteStyleTypeclass,
  ).styles;
  const stylesReadonly = styles.map(approxSiteStyleToComponent);
  const domNodes = convertToSpacingBlocks(
    toNested(site.domNodes),
    stylesReadonly,
    (id) => (styles = addAffects(styles, pageId, id)),
  );
  const symbols = convertToSpacingBlocks(
    toNested(site.symbols),
    stylesReadonly,
    // This is kind of wrong.  I _think_ style usage of symbols should be recorded
    // against the page that they're used on.  But that's hard to calculate because
    // we don't have all symbol instances loaded (if multipage).
    //
    // Luckily, webflow recalculates `affects` if you open a page.
    (id) => (styles = addAffects(styles, pageId, id)),
  );
  return {
    ...site,
    domNodes: fromNested(domNodes),
    symbols: fromNested(symbols),
    styles: { ...site.styles, blocks: styles },
  };
}

/**
 * Convert a site that uses spacing blocks to use spacing wrappers.
 */
export function convertSiteToSpacingWrappers(site: Site, pageId: string): Site {
  let styles = mergeStyles(
    site.styles.blocks,
    MARGIN_STYLES,
    siteStyleTypeclass,
  ).styles;
  const stylesReadonly = styles.map(approxSiteStyleToComponent);
  const domNodes = convertToSpacingWrappers(
    toNested(site.domNodes),
    stylesReadonly,
    (id) => (styles = addAffects(styles, pageId, id)),
  );
  const symbols = convertToSpacingWrappers(
    toNested(site.symbols),
    stylesReadonly,
    // See the comment in convertSiteToSpacingBlocks
    (id) => (styles = addAffects(styles, pageId, id)),
  );
  return {
    ...site,
    domNodes: fromNested(domNodes),
    symbols: fromNested(symbols),
    styles: { ...site.styles, blocks: styles },
  };
}
